import { useEffect } from 'react';

import { RunningExperiments, useExperiments } from 'state/experiments';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

/**
 * Depending on the experiment variant, the user will see the following:
 *
 * | Variant    | Mobile (mobile web)    | Web                 |
 * |------------|------------------------|---------------------|
 * | Control    | Home Page Feed         | Home Page Feed      |
 * | Treatment  | News Feed              | News Feed           |
 *
 */
export enum ContentCardNotificationsExperimentVariants {
  CONTROL = 'control',
  TREATMENT = 'treatment',
}

type ContentCardsNotificationsExperimentResult = {
  isNewsFeedEnabled: boolean;
  isHomePageFeedEnabled: boolean;
  isExperimentEnabled: boolean;
};

type ResolveContentCardsNotificationsExperimentArgs = {
  showBrazeContentCards: boolean;
  isLoadingExperiment: boolean;
  isControl: boolean;
  isTreatment: boolean;
};

export const resolveContentCardsNotificationsExperiment = ({
  showBrazeContentCards,
  isLoadingExperiment,
  isControl,
  isTreatment,
}: ResolveContentCardsNotificationsExperimentArgs): ContentCardsNotificationsExperimentResult => {
  // Flag OFF. Always show Sanity Cards on Homepage. Don't show News section.
  if (!showBrazeContentCards) {
    return {
      isNewsFeedEnabled: false,
      isHomePageFeedEnabled: false,
      isExperimentEnabled: false,
    };
  }

  if (isLoadingExperiment) {
    return {
      isNewsFeedEnabled: false,
      isHomePageFeedEnabled: false,
      isExperimentEnabled: false,
    };
  }

  const hasResolvedExperimentVariant = isControl || isTreatment;
  const isNewsFeedEnabled = isTreatment;
  const isHomePageFeedEnabled = isControl || !hasResolvedExperimentVariant;
  const isExperimentEnabled = isNewsFeedEnabled || isHomePageFeedEnabled;

  return {
    isNewsFeedEnabled,
    isHomePageFeedEnabled,
    isExperimentEnabled,
  };
};

export const useContentCardsNotificationsExperiment = () => {
  const showBrazeContentCards = useFlag(LaunchDarklyFlag.SHOW_BRAZE_CONTENT_CARDS_ON_HOMEPAGE);

  const { isAssignmentLoading: isLoadingExperiment, variant } = useExperiments();

  const contentCardsExperiment = RunningExperiments.NotificationCenterTest;

  const isTreatment = variant(contentCardsExperiment, 'treatment', {
    skipExposureLogging: true,
  });

  const isControl = variant(contentCardsExperiment, 'control', {
    skipExposureLogging: true,
  });

  return resolveContentCardsNotificationsExperiment({
    showBrazeContentCards,
    isLoadingExperiment,
    isControl,
    isTreatment,
  });
};

/**
 * Logs Amplitude exposure for both control and treatment when a variant has resolved.
 */
export const useExposeContentCardsNotificationsExperiment = () => {
  const showBrazeContentCards = useFlag(LaunchDarklyFlag.SHOW_BRAZE_CONTENT_CARDS_ON_HOMEPAGE);
  const { isAssignmentLoading, variant, logExposure } = useExperiments();

  const shouldExpose =
    showBrazeContentCards &&
    !isAssignmentLoading &&
    (variant(RunningExperiments.NotificationCenterTest, 'treatment', {
      skipExposureLogging: true,
    }) ||
      variant(RunningExperiments.NotificationCenterTest, 'control', {
        skipExposureLogging: true,
      }));

  useEffect(() => {
    if (!shouldExpose) {
      return;
    }

    logExposure(RunningExperiments.NotificationCenterTest);
  }, [shouldExpose, logExposure]);
};
